import { DatePipe } from '@angular/common'
import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { CurrentDeviceService } from './current-device.service'
import { DateService } from './date.service'

export type TypeNames<T> = T extends string | number | symbol ? T : keyof T

export interface DayOption {
  name: string
  value: number
}

interface EmployeeEditEvent {
  action: string
  time: string
  create_time: string
  user_name: string
}

type NestedNode = Record<string, unknown>

interface NestedTreeNode {
  data?: { id?: string }
  parent?: NestedTreeNode
}

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  // Cache for base64 images to avoid repeated downloads
  private imageCache = new Map<string, string>()

  constructor(
    private translate: TranslateService,
    private datePipe: DatePipe,
    public currentDeviceService: CurrentDeviceService,
  ) {}

  /**
   * Get days list
   * @param startedFrom started from by default is zero
   * @param everyDayOption With everyday option
   * @returns Days list
   */
  getDayList(startedFrom = 0, everyDayOption?: boolean): DayOption[] {
    const translate = this.translate
    const list: DayOption[] = []

    if (everyDayOption) {
      list.push({ name: translate.instant('COMMON.DAYS.EVERYDAY'), value: -1 })
    }

    const days = ['COMMON.DAYS.SUNDAY', 'COMMON.DAYS.MONDAY', 'COMMON.DAYS.TUESDAY', 'COMMON.DAYS.WEDNESDAY', 'COMMON.DAYS.THURSDAY', 'COMMON.DAYS.FRIDAY', 'COMMON.DAYS.SATURDAY']

    days.forEach((day, index) => {
      list.push({
        name: translate.instant(day),
        value: startedFrom + index + 1,
      })
    })

    return list
  }

  /**
   * Sort and return object
   * @param object Object to sort
   * @returns Sorted object
   */
  getSortedObject(input: object): Record<string, unknown> {
    const object = input as Record<string, unknown>
    const toReturn: Record<string, unknown> = {}

    for (const i in object) {
      if (!Object.hasOwn(object, i)) continue

      if (typeof object[i] == 'object') {
        const flatObject = this.getSortedObject(object[i] as object)
        for (const x in flatObject) {
          if (!Object.hasOwn(flatObject, x)) continue

          toReturn[i + '.' + x] = flatObject[x]
        }
      } else {
        toReturn[i] = object[i]
      }
    }
    return toReturn
  }

  /**
   * Get short time to show in avatar
   * @param fullName Full name
   * @returns Short name
   */
  getShortName(fullName: string) {
    return fullName
      ?.split(' ')
      .map((n) => n[0])
      .join('')
  }

  /**
   * Get minimum data by minimum number, when the number of the data in bigger than minNumber parameter only return first "minNumber" items else return all
   * @param arrayOfObjects Array of objects
   * @returns All items if the length of array lower that 3 and only three if bigger than 3
   */
  minimumData<T>(arrayOfObjects: T[], minNumber: number): T[] {
    const arr = Object.assign([], arrayOfObjects) as T[]

    if (arr.length > minNumber) {
      return arr.slice(0, minNumber)
    }
    return arr
  }

  /**
   * Get remained data length with plus
   * @param arrayOfObjects Array of objects
   * @returns Remained data length with plus (+)
   */
  remainedData<T>(arrayOfObjects: T[], minNumber: number) {
    const remainedData = arrayOfObjects.length - this.minimumData(arrayOfObjects, minNumber).length
    return remainedData ? `+${remainedData}` : null
  }

  /**
   * Get remained data tooltip names to show in tooltip
   * @param arrayOfObjects Array of objects
   * @param searchByKeyName Search by key name, like name, id ..
   * @returns Tooltip names
   */
  remainedDataTooltipNames(arrayOfObjects: unknown[], searchByKeyName?: string, minNumber?: number) {
    let tooltip = ''

    for (let i = minNumber ?? 0; i < arrayOfObjects.length; i++) {
      if (searchByKeyName) {
        tooltip += `${(arrayOfObjects[i] as Record<string, unknown>)[searchByKeyName]}${i == arrayOfObjects.length - 1 ? '.' : ', '}`
      } else {
        tooltip += `${arrayOfObjects[i]}${i == arrayOfObjects.length - 1 ? '.' : ', '}`
      }
    }
    return tooltip
  }

  /**
   * Init and get station container tooltip
   * @param root Root html element
   * @returns Station container tooltip
   */
  initStationContainerTooltip(root?: HTMLElement): HTMLElement {
    root = document.createElement('div')
    root.style.background = '#FEFEFE'
    root.style.borderRadius = '10px'
    root.style.color = 'black'
    root.style.boxShadow = '0px 8px 30px rgba(199, 203, 226, 0.5)'
    root.style.opacity = '1'
    root.style.pointerEvents = 'none'
    root.style.position = 'absolute'
    root.style.transform = 'translate(-50%, 0)'
    root.style.transition = 'all .1s ease'
    root.innerHTML = `<div style="width:${this.currentDeviceService && this.currentDeviceService.isMobile ? '150px' : '350px'};padding:10px;"></div>`

    return root
  }

  /**
   * Init and get station content tooltip
   * @param data Data to show header content footer
   * @param root Root html element
   * @returns Station content tooltip
   */
  initStationContentTooltip(
    data: {
      id: string
      header: {
        key: { img: string; value: unknown }
        value: { value: unknown; icon: string; iconColor: string }
      }
      content: { key: unknown; value: unknown }[]
      footer: { key: { value: unknown; icon: string }; value: unknown }[]
    },
    root: HTMLElement & { yAlign?: string },
  ) {
    // Init header
    let innerHtml = '<div class="row mb-5">'
    innerHtml += `<div class="row-inline">
                    <img src="${data.header.key.img}"
                    class="mr-5" width="${this.currentDeviceService && this.currentDeviceService.isMobile ? 16 : 32}" height="${
                      this.currentDeviceService && this.currentDeviceService.isMobile ? 16 : 32
                    }" alt="${data.header.key.img}"
                    title="${data.header.key.img}" /><b>${data.header.key.value}</b>
                    </div>
                    <div class="row-inline">
                    <span class="material-symbols-outlined" style="color:${data.header.value.iconColor};font-size:1em">
                      ${data.header.value.icon}
                    </span>
                    <span style="color:${data.header.value.iconColor}">${data.header.value.value}</span>
                    </div>
                    </div>`

    // Init content
    data.content.forEach((ketValue) => {
      innerHtml += `<div class="row mb-5"><div class="fz-13">${ketValue.key}</div><b>${ketValue.value}</b></div>`
    })

    // Init footer
    innerHtml += '<div class="divider-h mb-5"></div>'
    data.footer.forEach((ketValue) => {
      innerHtml += '<div class="row mb-5">'
      innerHtml += `<div class="row-inline fz-13"><span class="mr-5"><i style="font-size:0.75em" class="${ketValue.key.icon}"></i/></span>
                      <span>${ketValue.key.value}</span></div>
                      <div class="fz-13">${ketValue.value}</div>
                      </div>`
    })

    // Set caret Position
    root.classList.remove('above', 'below', 'no-transform')
    if (root.yAlign) {
      root.classList.add(root.yAlign)
    } else {
      root.classList.add('no-transform')
    }
    return innerHtml
  }

  /**
   * Init and return employee edited event tooltip data
   * @param notes Notes
   */
  initEmployeeEventTypeTooltip(notes: string, dateService: DateService) {
    let innerHTML = `<div class="row-inline">
        <i class="pi pi-pencil color-primary pr-5"></i>
        <span class="fz-18"><b>${this.translate.instant('EMPLOYEES.LABELS.EDITED_EVENT')}</b></span>
    </div>`
    innerHTML += '<div class="divider-h mb-5 mt-5"></div>'
    const obj = JSON.parse(notes) as EmployeeEditEvent[]

    obj.forEach((element, index) => {
      let newValue =
        element.action != 'vacation_day' && element.action != 'sick_day' && element.action != 'cancel_day' && element.action != 'delete_log'
          ? this.datePipe.transform(dateService.convertToLocalDate(element.time), 'HH:mm')
          : this.translate.instant('EMPLOYEES.ATTENDANCE_STATUS.' + element.action.replace('_', '').toUpperCase())

      if (index == obj.length || index == 0) {
        innerHTML +=
          '<h4 class="no-mg">' +
          this.translate.instant('EMPLOYEES.REPORT_MESSAGES.EDITED_EVENT_BY_USER', {
            changeDate: this.datePipe.transform(dateService.convertToLocalDate(element.create_time), 'dd/MM/yyyy : HH:mm'),
            username: element.user_name,
            action: this.translate.instant('EMPLOYEES.ATTENDANCE_STATUS.' + element.action.replace('_', '').toUpperCase()),
            newValue: newValue,
          }) +
          '</h4>'
      } else {
        let isDelete = false
        let action = element.action
        if (element.action == 'vacation_day' || element.action == 'sick_day') {
          action = 'shift'
        } else if (element.action == 'cancel_day') {
          action = obj[index - 1].action
          newValue = this.translate.instant('EMPLOYEES.ATTENDANCE_STATUS.SHIFT')
        } else if (element.action == 'delete_log') {
          isDelete = true
          innerHTML +=
            '<h4 class="no-mg">' +
            this.translate.instant('EMPLOYEES.REPORT_MESSAGES.DELETED_DAY_EVENT', {
              changeDate: this.datePipe.transform(dateService.convertToLocalDate(element.create_time), 'dd/MM/yyyy'),
              username: element.user_name,
            }) +
            '</h4>'
        }

        if (!isDelete) {
          innerHTML +=
            '<h4 class="no-mg">' +
            this.translate.instant('EMPLOYEES.REPORT_MESSAGES.EDITED_EVENT_BY_USER', {
              changeDate: this.datePipe.transform(dateService.convertToLocalDate(element.create_time), 'dd/MM/YYYY'),
              username: element.user_name,
              action: this.translate.instant('EMPLOYEES.ATTENDANCE_STATUS.' + action.replace('_', '').toUpperCase()),
              newValue: newValue,
            }) +
            '</h4>'
        }
      }

      if (index != obj?.length - 1) {
        innerHTML += '<span style="height:10px">&nbsp;</span>'
      }
    })
    return innerHTML
  }

  /**
   * Convert string to array
   * @param stringValue String value
   * @returns Array of strings
   */
  convertStringToArray(stringValue: string): string[] {
    if (
      /^[\],:{}\s]*$/.test(
        stringValue
          .replace(/\\["\\/bfnrtu]/g, '@')
          .replace(/"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g, ']')
          .replace(/(?:^|:|,)(?:\s*\[)+/g, ''),
      )
    ) {
      return JSON.parse(stringValue) as string[]
    } else {
      stringValue = stringValue.replace('[', '')
      stringValue = stringValue.replace(']', '')
      stringValue = stringValue.replace('"', '')
      return stringValue.split(',')
    }
  }

  /**
   * Convert image to base 64 (with caching to avoid repeated downloads)
   * @param imageUrl
   * @returns
   */
  async getBase64ImageFromUrl(imageUrl: string) {
    // Check cache first
    if (this.imageCache.has(imageUrl)) {
      return this.imageCache.get(imageUrl)
    }

    try {
      const res = await fetch(imageUrl, { cache: 'force-cache' })
      if (!res.ok) {
        console.warn(`Failed to fetch image from ${imageUrl}: ${res.status} ${res.statusText}`)
        return null
      }
      const blob = await res.blob()

      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener(
          'load',
          () => {
            const result = reader.result as string
            // Store in cache
            this.imageCache.set(imageUrl, result)
            resolve(result)
          },
          false,
        )

        reader.onerror = () => {
          return reject(this)
        }

        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.warn(`Error fetching image from ${imageUrl}:`, error)
      return null
    }
  }

  /**
   * Base 64 to blob convert
   * @param b64Data
   * @param sliceSize
   * @returns
   */
  base64ToBlob(b64Data: string, sliceSize = 512) {
    const byteCharacters = atob(b64Data) //data.file there
    const byteArrays = []
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }
    return new Blob(byteArrays, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
  }

  /**
   * Copy text to clipboard - Safari macOS specific implementation
   * @param val Value to copy
   */
  copyTextToClipboard(val: string): Promise<boolean> {
    return new Promise((resolve) => {
      // Check for Safari on macOS
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      const isMac = /Mac OS X/.test(navigator.userAgent)

      if (isMac && isSafari) {
        // Safari on Mac specific approach
        try {
          // Create an off-screen input element
          const el = document.createElement('input')
          el.value = val
          el.setAttribute('readonly', '')
          el.style.position = 'absolute'
          el.style.left = '-9999px'
          document.body.appendChild(el)

          // Make the element visible to Safari (important)
          el.style.display = 'block'

          // Create a selection range
          const selection = document.getSelection()
          const selected = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : false

          // Select the text field
          el.select()
          el.setSelectionRange(0, 99999) // For mobile devices

          // Execute copy command with a small delay
          setTimeout(() => {
            const successful = document.execCommand('copy')
            document.body.removeChild(el)

            // Restore original selection if any
            if (selected) {
              const currentSelection = document.getSelection()
              currentSelection?.removeAllRanges()
              currentSelection?.addRange(selected)
            }

            resolve(successful)
          }, 100)
        } catch (err) {
          console.error('Safari copy failed:', err)
          resolve(false)
        }
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        // Modern browsers
        navigator.clipboard
          .writeText(val)
          .then(() => resolve(true))
          .catch(() => {
            // Fallback for other browsers
            const textArea = document.createElement('textarea')
            textArea.value = val
            textArea.style.position = 'fixed'
            textArea.style.left = '0'
            textArea.style.top = '0'
            textArea.style.opacity = '0'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()

            try {
              const successful = document.execCommand('copy')
              document.body.removeChild(textArea)
              resolve(successful)
            } catch (_err) {
              document.body.removeChild(textArea)
              resolve(false)
            }
          })
      } else {
        // Old browsers fallback
        const textArea = document.createElement('textarea')
        textArea.value = val
        textArea.style.position = 'fixed'
        textArea.style.left = '0'
        textArea.style.top = '0'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        try {
          const successful = document.execCommand('copy')
          document.body.removeChild(textArea)
          resolve(successful)
        } catch (_err) {
          document.body.removeChild(textArea)
          resolve(false)
        }
      }
    })
  }

  /**
   * Clean the empty properties from object
   * @param obj
   * @returns
   */
  cleanEmptyProperties<T extends object>(obj: T): T {
    const record = obj as Record<string, unknown>
    for (const propName in record) {
      if (record[propName] === '' || record[propName] === null) {
        delete record[propName]
      }
    }
    return obj
  }

  /**
   * Find the nested object recursive
   * @param obj The selected object to check
   * @param value The value
   * @param deepKey The deep key
   * @param checkKey The key to check
   * @returns
   */
  findNestedObjectById(obj: unknown, value: string, deepKey: string, checkKey: string): NestedNode | null {
    // Check if the current object has the desired ID
    const parent = (obj as NestedNode[] | undefined)?.find((v) => v[checkKey] === value || v[deepKey] === value)
    if (parent) return parent

    // Check if the current object has nested objects
    const record = obj as Record<string, NestedNode>
    for (const key in record) {
      // Recursively search in nested objects
      const result = this.findNestedObjectById(record[key][deepKey], value, deepKey, checkKey)
      if (result) return result
    }
    // If the ID is not found, return null or handle the case accordingly
    return null
  }

  /**
   * Delete the nested object recursive
   * @param obj The selected object to check
   * @param deepKey The deep key
   * @param checkKey The key to check
   * @returns
   */
  deleteNestedObject(array: NestedNode[], value: unknown, deepKey: string, checkKey: string): unknown {
    // Check if the current object has the desired ID
    const parent = array?.find((v) => v[checkKey] === value || v[deepKey] === value)
    const index: number = array.indexOf(parent as NestedNode)
    if (parent) {
      array.splice(index, 1)
      return value
    }

    // Check if the current object has nested objects
    for (const key in array) {
      // Recursively search in nested objects
      const result = this.deleteNestedObject((array as unknown as Record<string, NestedNode>)[key][deepKey] as NestedNode[], value, deepKey, checkKey)
      if (result) {
        array.splice(result as number, 1)
        return value
      }
    }
    // If the ID is not found, return null or handle the case accordingly
    return null
  }

  /**
   * get ids list from nested array of objects
   * @param array
   * @param deepKey
   * @param checkKey
   * @param ids
   * @returns
   */
  getListIdsFromNestedArrayOfObjects(array: NestedTreeNode[], ids: string[]): string[] {
    // Check if the current object has nested objects
    for (const item of array) {
      ids.push(item?.data?.id as string)

      this.getParentIds(item.parent, ids)
    }

    return ids
  }

  getParentIds(node: NestedTreeNode | null | undefined, ids: string[]) {
    ids.push(node?.data?.id as string)

    while (node?.parent) {
      this.getParentIds(node?.parent, ids)
      if (!node?.parent) {
        return ids
      }
    }
    return []
  }

  /**
   * Get value from nested object
   * @param obj
   * @param propertyString
   * @returns
   */
  getValueFromNestedObject(obj: unknown, propertyString: string): unknown {
    const properties = propertyString.split('.')
    let value: unknown = obj

    for (const property of properties) {
      value = (value as Record<string, unknown>)[property]
      if (!value) {
        return undefined // Return undefined if the property doesn't exist
      }
    }
    return value
  }

  checkObjectValues(obj: object) {
    const record = obj as Record<string, unknown>
    for (const key in record) {
      if (Object.hasOwn(record, key) && record[key] !== null && record[key] !== undefined && record[key] !== '') {
        return true // If any key has a value, return true
      }
    }
    return false // If no keys have a value, return false
  }

  getTypeNames<T>(input: T): TypeNames<T>[] {
    if (typeof input === 'string' || typeof input === 'number' || typeof input === 'symbol') {
      return [input] as TypeNames<T>[]
    }

    return Object.keys(input as object) as TypeNames<T>[]
  }

  getObjectDiff(obj1: object, obj2: object): Record<string, unknown> {
    const a = obj1 as Record<string, unknown>
    const b = obj2 as Record<string, unknown>
    const diff: Record<string, unknown> = {}

    for (const key in a) {
      if (Object.hasOwn(a, key) && a[key] !== b[key]) {
        diff[key] = b[key]
      }
    }

    return diff
  }

  addCharInCenter(inputString: string, charToAdd: string): string {
    const middleIndex = Math.floor(inputString.length / 2)
    const firstHalf = inputString.slice(0, middleIndex)
    const secondHalf = inputString.slice(middleIndex)

    return firstHalf + charToAdd + secondHalf
  }

  areObjectsEqual(obj1: object, obj2: object, excludedProps: string[]): boolean {
    // Exclude specified properties from comparison
    const excludedProperties = excludedProps
    const a = obj1 as Record<string, unknown>
    const b = obj2 as Record<string, unknown>

    for (const prop in a) {
      if (!excludedProperties.includes(prop) && (a[prop] as { toString(): string })?.toString() != (b[prop] as { toString(): string })?.toString()) {
        return false
      }
    }

    return true
  }

  findSimilarObjects<T extends object>(array: T[], excludedProps: string[]): T[][] {
    const result: T[][] = []

    for (let i = 0; i < array.length; i++) {
      const currentObject = array[i]
      const similarObjects = [currentObject]

      for (let j = i + 1; j < array.length; j++) {
        const nextObject = array[j]

        if (this.areObjectsEqual(currentObject, nextObject, excludedProps)) {
          similarObjects.push(nextObject)
          array.splice(j, 1)
          j-- // Adjust index after removal
        }
      }

      if (similarObjects.length > 1) {
        result.push(similarObjects)
      }
    }

    return result
  }
}
